import toast from "react-hot-toast";

/**
 * Toast debouncer to avoid multiple simultaneous session expired toasts
 */
let lastToastTime = 0;
const TOAST_COOLDOWN = 3000;

export const showSessionToast = (message) => {
  const now = Date.now();
  if (now - lastToastTime > TOAST_COOLDOWN) {
    lastToastTime = now;
    toast.error(message, {
      id: "session-expired-toast",
      duration: 4500,
    });
  }
};

/**
 * Safely parse JWT token payload
 */
export const parseJwt = (token) => {
  if (!token || typeof token !== "string") return null;
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const base64Url = parts[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    return null;
  }
};

/**
 * Check if a JWT token is expired
 * @param {string} token
 * @param {number} bufferSeconds - optional safety buffer in seconds (default 5s)
 */
export const isTokenExpired = (token, bufferSeconds = 5) => {
  if (!token) return true;
  const decoded = parseJwt(token);
  if (!decoded || !decoded.exp) return false; // If no exp claim, cannot determine from token
  const currentTime = Math.floor(Date.now() / 1000);
  return decoded.exp - bufferSeconds <= currentTime;
};

/**
 * Get remaining validity time in milliseconds
 */
export const getTokenRemainingTime = (token) => {
  if (!token) return 0;
  const decoded = parseJwt(token);
  if (!decoded || !decoded.exp) return null;
  const remaining = decoded.exp * 1000 - Date.now();
  return Math.max(0, remaining);
};

/* ---------------- Activity Tracking ---------------- */
export const LAST_ACTIVE_KEY = "last_active_time";

export const updateLastActivityTime = () => {
  try {
    localStorage.setItem(LAST_ACTIVE_KEY, Date.now().toString());
  } catch (e) {
    // ignore
  }
};

export const getLastActivityTime = () => {
  try {
    const timeStr = localStorage.getItem(LAST_ACTIVE_KEY);
    return timeStr ? parseInt(timeStr, 10) : Date.now();
  } catch (e) {
    return Date.now();
  }
};

/* ---------------- Session Clear Helpers ---------------- */

export const clearUserSession = () => {
  localStorage.removeItem("userToken");
  localStorage.removeItem("token");
  localStorage.removeItem("user");
  localStorage.removeItem("userId");
  localStorage.removeItem("userName");
  localStorage.removeItem("userEmail");
  localStorage.removeItem("userPhone");
  if (localStorage.getItem("role") === "patient" || localStorage.getItem("role") === "user") {
    localStorage.removeItem("role");
  }
};

export const clearSellerSession = () => {
  localStorage.removeItem("sellerToken");
  localStorage.removeItem("seller");
  localStorage.removeItem("sellerId");
  if (localStorage.getItem("role") === "seller") {
    localStorage.removeItem("role");
  }
};

export const clearCentreSession = () => {
  localStorage.removeItem("centreToken");
  localStorage.removeItem("centre");
  localStorage.removeItem("centreId");
  if (localStorage.getItem("role") === "centre") {
    localStorage.removeItem("role");
  }
};

export const clearAdminSession = () => {
  localStorage.removeItem("adminToken");
  localStorage.removeItem("adminId");
  localStorage.removeItem("adminRole");
  if (localStorage.getItem("role") === "admin") {
    localStorage.removeItem("role");
  }
};

export const clearAllSessions = () => {
  clearUserSession();
  clearSellerSession();
  clearCentreSession();
  clearAdminSession();
};

/* ---------------- Notification & Dispatch ---------------- */

export const notifyAuthChange = () => {
  try {
    window.dispatchEvent(new Event("userLogin"));
    window.dispatchEvent(new CustomEvent("auth:change", { detail: { timestamp: Date.now() } }));
  } catch (e) {
    // ignore
  }
};

/* ---------------- Login State Checkers ---------------- */

export const isUserLoggedIn = () => {
  const token = localStorage.getItem("userToken") || (localStorage.getItem("role") === "patient" ? localStorage.getItem("token") : null);
  return Boolean(token && !isTokenExpired(token));
};

export const isSellerLoggedIn = () => {
  const token = localStorage.getItem("sellerToken") || (localStorage.getItem("role") === "seller" ? localStorage.getItem("token") : null);
  return Boolean(token && !isTokenExpired(token));
};

export const isCentreLoggedIn = () => {
  const token = localStorage.getItem("centreToken") || (localStorage.getItem("role") === "centre" ? localStorage.getItem("token") : null);
  return Boolean(token && !isTokenExpired(token));
};

export const isAdminLoggedIn = () => {
  const token = localStorage.getItem("adminToken");
  return Boolean(token && !isTokenExpired(token));
};

export const isAnySessionActive = () => {
  return isUserLoggedIn() || isSellerLoggedIn() || isCentreLoggedIn() || isAdminLoggedIn();
};

/* ---------------- Explicit & Auto Logout Actions ---------------- */

/**
 * Logout User
 * @param {Object} options - { reason: "manual"|"expired"|"idle", notify: boolean, navigate: function, redirectUrl: string }
 */
export const logoutUser = (options = {}) => {
  const {
    reason = "manual",
    notify = reason !== "manual",
    navigate = null,
    redirectUrl = "/shop",
  } = options;

  clearUserSession();
  notifyAuthChange();

  if (notify) {
    const msg =
      reason === "idle"
        ? "User session timed out due to inactivity. Please log in again."
        : "User session expired. Please log in again.";
    showSessionToast(msg);
  }

  // Redirect if currently on a protected user route
  const currentPath = window.location.pathname;
  const isUserRoute =
    currentPath.includes("user-dashboard") ||
    currentPath.includes("user-profile") ||
    currentPath.includes("medical-history") ||
    currentPath.includes("booking-report") ||
    currentPath.includes("add-insurance") ||
    currentPath.includes("appointments") ||
    currentPath.includes("requests") ||
    currentPath.includes("orders") ||
    currentPath.includes("user-helpAndSupport");

  if (isUserRoute || reason === "manual") {
    if (navigate) {
      navigate(redirectUrl);
    } else if (window.location.pathname !== redirectUrl) {
      window.location.href = redirectUrl;
    }
  }
};

/**
 * Logout Seller
 * @param {Object} options - { reason: "manual"|"expired"|"idle", notify: boolean, navigate: function, redirectUrl: string }
 */
export const logoutSeller = (options = {}) => {
  const {
    reason = "manual",
    notify = reason !== "manual",
    navigate = null,
    redirectUrl = "/shop",
  } = options;

  clearSellerSession();
  notifyAuthChange();

  if (notify) {
    let msg;
    if (reason === "blocked") {
      msg = "Your account has been blocked by the Admin. Kindly contact the Admin for further assistance.";
    } else if (reason === "idle") {
      msg = "Seller session timed out due to inactivity. Please log in again.";
    } else {
      msg = "Seller session expired. Please log in again.";
    }
    showSessionToast(msg);
  }

  // Redirect if currently on a protected seller route
  const currentPath = window.location.pathname;
  const isSellerRoute =
    currentPath.includes("seller-dashboard") ||
    currentPath.includes("inventory") ||
    currentPath.includes("stock-alerts") ||
    currentPath.includes("active-orders") ||
    currentPath.includes("completed-orders") ||
    currentPath.includes("cancelled-orders") ||
    currentPath.includes("delivery-zones") ||
    currentPath.includes("delivery-settings") ||
    currentPath.includes("seller-profile") ||
    currentPath.includes("seller-settings") ||
    currentPath.includes("add-product") ||
    currentPath.includes("seller-products");

  if (isSellerRoute || reason === "manual") {
    if (navigate) {
      navigate(redirectUrl);
    } else if (window.location.pathname !== redirectUrl) {
      window.location.href = redirectUrl;
    }
  }
};

/**
 * Logout Centre
 */
export const logoutCentre = (options = {}) => {
  const {
    reason = "manual",
    notify = reason !== "manual",
    navigate = null,
    redirectUrl = "/",
  } = options;

  clearCentreSession();
  notifyAuthChange();

  if (notify) {
    const msg =
      reason === "idle"
        ? "Centre session timed out due to inactivity. Please log in again."
        : "Centre session expired. Please log in again.";
    showSessionToast(msg);
  }

  const currentPath = window.location.pathname;
  const isCentreRoute =
    currentPath.includes("centre-dashboard") ||
    currentPath.includes("dashboard-profile") ||
    currentPath.includes("doctors") ||
    currentPath.includes("package") ||
    currentPath.includes("follower") ||
    currentPath.includes("statement") ||
    currentPath.includes("earning") ||
    currentPath.includes("refund") ||
    currentPath.includes("create-doctor") ||
    currentPath.includes("create-service") ||
    currentPath.includes("create-package");

  if (isCentreRoute || reason === "manual") {
    if (navigate) {
      navigate(redirectUrl);
    } else if (window.location.pathname !== redirectUrl) {
      window.location.href = redirectUrl;
    }
  }
};

/**
 * Logout Admin
 */
export const logoutAdmin = (options = {}) => {
  const {
    reason = "manual",
    notify = reason !== "manual",
    navigate = null,
    redirectUrl = "/manage4ceps-admin",
  } = options;

  clearAdminSession();
  notifyAuthChange();

  if (notify) {
    const msg =
      reason === "idle"
        ? "Admin session timed out due to inactivity. Please log in again."
        : "Admin session expired. Please log in again.";
    showSessionToast(msg);
  }

  const currentPath = window.location.pathname;
  const isAdminRoute =
    currentPath.includes("admin") ||
    currentPath.includes("manage4ceps") ||
    currentPath.includes("seller-approved") ||
    currentPath.includes("adminOrders") ||
    currentPath.includes("adminBooking");

  if (isAdminRoute || reason === "manual") {
    if (navigate) {
      navigate(redirectUrl);
    } else if (window.location.pathname !== redirectUrl) {
      window.location.href = redirectUrl;
    }
  }
};
